import { useState, useEffect, useRef, useCallback } from "react";
import { SuccessView, Spinner } from "../components/support";
import { usePrivy } from "@privy-io/react-auth";
import { NotLoggedIn } from "@/components/support";
import { API_BASE_URL } from "@/constants";
import { isAppWebView } from "@/utils/webview";

type AuthMethod = "Token" | "Privy";

const MAX_ATTACHMENTS = 3;
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB
const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"] as const;
type AcceptedType = (typeof ACCEPTED_TYPES)[number];

interface Attachment {
  filename: string;
  contentType: AcceptedType;
  data: string; // raw base64, no data URL prefix
  previewUrl: string; // object URL for thumbnail
}

const SUBJECTS = [
  "Account & Verification",
  "Deposits & Withdrawals",
  "App Technical Issue",
  "Security Concern",
  "Other",
];

function decodeAuthToken(rawToken: string): string {
  const direct = rawToken.trim();
  const jwtLike = /^[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+$/;
  if (jwtLike.test(direct)) return direct;
  try {
    return atob(direct);
  } catch {
    // Continue to normalized base64url decode attempt.
  }

  // URL/form decoding can convert "+" into spaces, so restore "+" before base64 decode.
  const normalized = direct
    .replace(/-/g, "+")
    .replace(/_/g, "/")
    .replace(/\s/g, "+");
  try {
    const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
    return atob(padded);
  } catch {
    // Some flows may already pass a raw bearer token.
    return direct;
  }
}

async function fetchEmailFromToken(token: string): Promise<string | null> {
  try {
    const normalizedRaw = token.trim().replace(/\s/g, "+");
    const candidates = [normalizedRaw];
    const decoded = decodeAuthToken(token);
    if (decoded && decoded !== candidates[0]) candidates.push(decoded);

    for (const candidate of candidates) {
      const res = await fetch(`${API_BASE_URL}/v3/auth`, {
        headers: { Authorization: `Bearer ${candidate}` },
      });
      if (!res.ok) continue;
      const data = await res.json() as { user?: { email?: string } };
      const email = data.user?.email ?? null;
      if (email) return email;
    }

    return null;
  } catch {
    return null;
  }
}

export default function Support() {
  const { ready, authenticated, user, logout } = usePrivy();
  const urlParams = new URLSearchParams(window.location.search);
  const authCode = urlParams.get("auth_code");
  const hasAuthCode = Boolean(authCode);
  // Some embedded webviews do not reliably expose the bridge object.
  // If auth_code is present, treat it as app-webview mode for auth gating.
  const isWebView = isAppWebView() || hasAuthCode;

  
  // null = not yet resolved, "" = resolved but empty
  const [email, setEmail] = useState<string | null>(null);
  const [authMethod, setAuthMethod] = useState<AuthMethod | null>(null);
  // true only while an auth_code param fetch is in flight
  const [tokenResolving, setTokenResolving] = useState(
    () => hasAuthCode && isWebView
  );

  const [subject, setSubject] = useState(SUBJECTS[0]);
  const [message, setMessage] = useState("");
  const [messageError, setMessageError] = useState<string | null>(null);

  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const widgetContainerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [tokenInvalid, setTokenInvalid] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [caseId, setCaseId] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Inject Turnstile script once on mount (skipped in app webview — captcha is web-only)
  useEffect(() => {
    if (isWebView) return;
    if (document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]')) return;
    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    document.head.appendChild(script);

    return () => {
      if (widgetIdRef.current) window.turnstile?.remove(widgetIdRef.current);
    };
  }, [isWebView]);

  const clearLocalAuthState = useCallback(() => {
    setEmail(null);
    setAuthMethod(null);
    setTokenInvalid(false);
  }, []);

  useEffect(() => {
    if (authCode) {
      if (!isWebView) {
        setTokenInvalid(true);
        setTokenResolving(false);
        return;
      }
      fetchEmailFromToken(authCode).then((resolved) => {
        if (resolved) {
          setTokenInvalid(false);
          setEmail(resolved);
          setAuthMethod("Token");
        } else {
          setTokenInvalid(true);
        }
        setTokenResolving(false);
      });
      return;
    }

    if (ready && authenticated && user?.email?.address) {
      setEmail(user.email.address);
      setAuthMethod("Privy");
      return;
    }

    if (ready && !authenticated) {
      clearLocalAuthState();
    }
  }, [ready, authenticated, user, authCode, isWebView, clearLocalAuthState]);

  const isAuthenticated = authMethod !== null;
  // Still waiting on token fetch or Privy initialisation
  const resolving = tokenResolving || (!hasAuthCode && !ready);

  // Render Turnstile widget once the gate lifts
  useEffect(() => {
    if (isWebView) return;
    if (!isAuthenticated || !widgetContainerRef.current || widgetIdRef.current) return;

    const doRender = () => {
      const sitekeyVal: string | undefined = process.env.BUN_PUBLIC_TURNSTILE_SITE_KEY;
      if (!sitekeyVal) {
        console.error("Missing BUN_PUBLIC_TURNSTILE_SITE_KEY environment variable");
        return;
      }
      if (window.turnstile && widgetContainerRef.current && !widgetIdRef.current) {
        const renderOptions: { sitekey: string; callback: (token: string) => void; "expired-callback": () => void; "error-callback": () => void; theme: "light" } = {
          sitekey: sitekeyVal!,
          callback: (token: string) => setTurnstileToken(token),
          "expired-callback": () => setTurnstileToken(null),
          "error-callback": () => setTurnstileToken(null),
          theme: "light",
        };
        const result = window.turnstile.render(widgetContainerRef.current, renderOptions);
        widgetIdRef.current = result ?? null;
      }
    };

    if (window.turnstile) {
      doRender();
    } else {
      const interval = setInterval(() => {
        if (window.turnstile) { doRender(); clearInterval(interval); }
      }, 100);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, isWebView]);

  const handleFilesSelected = useCallback((files: FileList | null) => {
    if (!files) return;
    setAttachmentError(null);

    const incoming = Array.from(files);
    const slots = MAX_ATTACHMENTS - attachments.length;

    if (slots <= 0) {
      setAttachmentError(`You can attach up to ${MAX_ATTACHMENTS} images.`);
      return;
    }

    const accepted = incoming.slice(0, slots);
    const rejected = incoming.slice(slots);

    if (rejected.length > 0) {
      setAttachmentError(`Only ${MAX_ATTACHMENTS} images allowed. ${rejected.length} file(s) were skipped.`);
    }

    const toAdd: Attachment[] = [];
    const attachmentErrors: string[] = [];

    const promises = accepted.map(
      (file) =>
        new Promise<void>((resolve) => {
          if (!ACCEPTED_TYPES.includes(file.type as AcceptedType)) {
            attachmentErrors.push(`"${file.name}" is not a supported image type.`);
            resolve();
            return;
          }
          if (file.size > MAX_FILE_SIZE_BYTES) {
            attachmentErrors.push(`"${file.name}" exceeds the 5 MB limit.`);
            resolve();
            return;
          }
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            // Strip "data:<type>;base64," prefix
            const base64 = result.split(",")[1];
            toAdd.push({
              filename: file.name,
              contentType: file.type as AcceptedType,
              data: base64,
              previewUrl: URL.createObjectURL(file),
            });
            resolve();
          };
          reader.onerror = () => {
            attachmentErrors.push(`Failed to read file "${file.name}".`);
            resolve();
          };
          reader.readAsDataURL(file);
        })
    );

    Promise.all(promises).then(() => {
      if (attachmentErrors.length > 0) {
        const summary = attachmentErrors.length === 1
          ? attachmentErrors[0]
          : `${attachmentErrors.length} files failed: ${attachmentErrors.join("; ")}`;
        setAttachmentError(summary);
      }
      if (toAdd.length > 0) setAttachments((prev) => [...prev, ...toAdd]);
    });
  }, [attachments.length]);

  const removeAttachment = useCallback((index: number) => {
    setAttachments((prev) => {
      const next = [...prev];
      URL.revokeObjectURL(next[index].previewUrl);
      next.splice(index, 1);
      return next;
    });
    setAttachmentError(null);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmed = message.trim();
    if (!trimmed) {
      setMessageError("Message is required.");
      return;
    }
    if (trimmed.length < 10) {
      setMessageError("Message must be at least 10 characters.");
      return;
    }

    setSubmitError(null);
    setSubmitting(true);

    try {
      const res = await fetch("/api/support", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          subject,
          message: trimmed,
          authMethod,
          turnstileToken: isWebView ? null : turnstileToken,
          attachments: attachments.map(({ filename, contentType, data }) => ({ filename, contentType, data })),
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error((data as { error?: string }).error ?? "Server error — please try again.");
      setCaseId((data as { caseId: string }).caseId);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Network error — please try again.");
      // Reset widget so user can try again
      if (widgetIdRef.current) { window.turnstile?.reset(widgetIdRef.current); setTurnstileToken(null); }
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrivyLogout = async () => {
    try {
      await logout();
    } finally {
      clearLocalAuthState();
    }
  };

  if (caseId) return <SuccessView caseId={caseId} />;
  if (!isAuthenticated) return <NotLoggedIn resolving={resolving} tokenInvalid={tokenInvalid} />

  return (
    <div className="min-h-dvh bg-background text-foreground">

      <div className="max-w-[640px] mx-auto px-lg py-xl">
        <form onSubmit={handleSubmit} className="flex flex-col gap-lg">

          {/* Header */}
          <div className="flex items-start justify-between gap-md">
            <div className="flex flex-col gap-sm">
              <h1 className="text-lg font-bold">Contact Support</h1>
              <p className="text-base text-muted-fg leading-relaxed">
                We'll get back to you at your email address.
              </p>
            </div>
            {authMethod === "Privy" && !isWebView && (
              <button
                type="button"
                onClick={handlePrivyLogout}
                className="btn-primary shrink-0 flex items-center gap-[6px] h-[36px] px-[14px] rounded-full bg-secondary text-foreground text-sm font-medium cursor-pointer mt-[2px]"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                  <path d="M5 12H3a1 1 0 01-1-1V3a1 1 0 011-1h2M9.5 10l3-3-3-3M12.5 7H5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Sign out
              </button>
            )}
          </div>

          {/* Auth status badge */}
          {isAuthenticated && (
            <div className="flex items-center gap-sm px-md py-[10px] bg-secondary rounded-lg">
              <span className="w-[7px] h-[7px] shrink-0 rounded-full bg-success" aria-hidden="true" />
              <p className="text-sm text-muted-fg">
                Signed in as <strong className="text-foreground">{email}</strong>
              </p>
            </div>
          )}

          {/* Subject */}
          <div className="flex flex-col gap-xs">
            <label htmlFor="subject" className="text-sm font-medium text-foreground">Subject</label>
            <div className="relative">
              <select
                id="subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="h-[48px] w-full px-md rounded-lg border border-border bg-background text-base focus:outline-none focus:ring-2 focus:ring-foreground/20 appearance-none pr-[40px]"
              >
                {SUBJECTS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <span className="pointer-events-none absolute right-[14px] top-1/2 -translate-y-1/2 text-muted-fg">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                  <path d="M3 5L7 9L11 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
            </div>
          </div>

          {/* Message */}
          <div className="flex flex-col gap-xs">
            <label htmlFor="message" className="text-sm font-medium text-foreground">Message</label>
            <textarea
              id="message"
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
                if (messageError) setMessageError(null);
              }}
              placeholder="Describe your issue in detail…"
              rows={6}
              aria-invalid={!!messageError}
              className={`px-md py-[12px] rounded-lg border bg-background text-base placeholder:text-muted-fg focus:outline-none focus:ring-2 resize-none leading-relaxed ${
                messageError
                  ? "border-destructive focus:ring-destructive/20"
                  : "border-border focus:ring-foreground/20"
              }`}
            />
            {messageError && (
              <p className="text-sm text-destructive" role="alert">{messageError}</p>
            )}
          </div>

          {/* Attachments */}
          <div className="flex flex-col gap-xs">
            <div className="flex flex-col gap-[2px]">
              <span className="text-sm font-medium text-foreground">Attachments</span>
              <span className="text-sm text-muted-fg">Optional · Up to 3 images · 5 MB each</span>
            </div>

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="sr-only"
              aria-hidden="true"
              tabIndex={-1}
              onChange={(e) => {
                handleFilesSelected(e.target.files);
                // Reset so the same file can be re-selected after removal
                e.target.value = "";
              }}
            />

            {/* Thumbnails */}
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-sm">
                {attachments.map((att, i) => (
                  <div key={i} className="relative w-[72px] h-[72px] rounded-lg border border-border overflow-hidden bg-secondary shrink-0">
                    <img
                      src={att.previewUrl}
                      alt={att.filename}
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => removeAttachment(i)}
                      aria-label={`Remove ${att.filename}`}
                      className="absolute top-[3px] right-[3px] w-[18px] h-[18px] rounded-full bg-foreground/80 text-primary-fg flex items-center justify-center text-xs leading-none cursor-pointer hover:bg-foreground transition-colors"
                    >
                      x
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Pick button — only shown when slots remain */}
            {attachments.length < MAX_ATTACHMENTS && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="self-start flex items-center gap-sm h-[40px] px-md rounded-lg border border-border bg-background text-sm font-medium text-foreground cursor-pointer hover:bg-secondary transition-colors duration-150"
              >
                <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true">
                  <path d="M7.5 2v11M2 7.5h11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
                {attachments.length === 0 ? "Add images" : "Add more"}
              </button>
            )}

            {attachmentError && (
              <p className="text-sm text-destructive" role="alert">{attachmentError}</p>
            )}
          </div>

          {/* Turnstile widget — web only; app webview skips captcha */}
          {!isWebView && (
            <div className="flex justify-center"><div ref={widgetContainerRef} /></div>
          )}

          {/* Submit error */}
          {submitError && (
            <p className="text-sm text-destructive font-medium" role="alert" aria-live="assertive">{submitError}</p>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting || (!isWebView && !turnstileToken)}
            className="btn-primary h-[52px] w-full rounded-full bg-foreground text-primary-fg text-base font-semibold cursor-pointer shadow-[0_2px_8px_rgba(0,0,0,0.12)] mt-xs"
          >
            {submitting ? (
              <span className="flex items-center justify-center gap-sm">
                <Spinner />
                Sending…
              </span>
            ) : "Send Message"}
          </button>

        </form>
      </div>
    </div>
  );
}
